'use client';

import React from 'react';

// ============================================================================
// Size configurations (no Tailwind CSS dependency)
// ============================================================================

const SPINNER_SIZES = {
  xs: { width: '12px', height: '12px', borderWidth: '2px' },
  sm: { width: '16px', height: '16px', borderWidth: '2px' },
  md: { width: '24px', height: '24px', borderWidth: '2px' },
  lg: { width: '32px', height: '32px', borderWidth: '3px' },
  xl: { width: '48px', height: '48px', borderWidth: '4px' },
} as const;

// ============================================================================
// Keyframes style (injected once)
// ============================================================================

const SPINNER_KEYFRAMES = `
@keyframes tms-spinner-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

// Track if keyframes have been injected
let keyframesInjected = false;

function injectKeyframes() {
  if (typeof document === 'undefined' || keyframesInjected) return;

  const styleElement = document.createElement('style');
  styleElement.textContent = SPINNER_KEYFRAMES;
  document.head.appendChild(styleElement);
  keyframesInjected = true;
}

// ============================================================================
// Component
// ============================================================================

export interface SpinnerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  /**
   * The size of the spinner
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * The color of the spinner
   */
  color?: string;
  /**
   * The thickness of the spinner
   */
  thickness?: string;
  /**
   * The speed of the spinner animation
   */
  speed?: string;
  /**
   * The label for screen readers
   */
  label?: string;
  /**
   * Border width (internal use - don't pass to DOM)
   * @internal
   */
  borderWidth?: never;
}

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      size = 'md',
      color = 'currentColor',
      thickness,
      speed = '0.65s',
      label = 'Loading',
      style,
      // Extract component-specific props to prevent DOM errors
      borderWidth: _bw,
      ...domProps
    },
    ref
  ) => {
    // Inject keyframes on mount
    React.useEffect(() => {
      injectKeyframes();
    }, []);

    const sizeConfig = SPINNER_SIZES[size] || SPINNER_SIZES.md;

    const spinnerStyle: React.CSSProperties = {
      display: 'inline-block',
      width: sizeConfig.width,
      height: sizeConfig.height,
      borderRadius: '50%',
      borderStyle: 'solid',
      borderWidth: thickness || sizeConfig.borderWidth,
      borderColor: color,
      borderRightColor: 'transparent',
      animation: `tms-spinner-spin ${speed} linear infinite`,
      ...style,
    };

    return (
      <div ref={ref} style={spinnerStyle} role="status" aria-label={label} {...domProps}>
        <span
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {label}
        </span>
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';
